import Colors from '@components/shared/Colors';
import LinkTranslated from '@components/shared/LinkTranslated';
import { H3, P } from '@components/shared/Typography';
import useDesktop from '@components/shared/useDesktop';
import { Article } from '@contracts';
import { getCategoryTitle } from '@prismicVitals';
import { PrismicImage } from '@prismicio/react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

interface Props {
  article: Article;
  noMobileDesign?: boolean;
  callback?: () => void;
}

const BREAKPOINT = 1024;

const ArticleItem: React.FC<Props> = ({ article, noMobileDesign, callback = () => undefined }) => {
  const { featured_image, article_subheadline, title, category } = article.data;
  const desktop = useDesktop(BREAKPOINT);
  const [categoryTitle, setCategoryTitle] = useState('');
  const imageSize = desktop || noMobileDesign ? 340 : 160;

  useEffect(() => {
    getCategoryTitle(setCategoryTitle, category.id);
  }, []);

  return (
    <Container $noMobileDesign={noMobileDesign} className="article-container" data-testid="article-item">
      <InnerContainer $noMobileDesign={noMobileDesign}>
        <ImageWrapper $noMobileDesign={noMobileDesign}>
          <StyledLink to={`/vitals/${category.uid}/${article.uid}/`} aria-label={`Read ${title}`} onClick={() => callback()}>
            <PrismicImage field={featured_image} imgixParams={{ width: imageSize, height: imageSize }} alt={title} loading="lazy" />
          </StyledLink>
        </ImageWrapper>
        <TextWrapper>
          <CategoryName $noMobileDesign={noMobileDesign}>{categoryTitle}</CategoryName>
          <ArticleTitle>
            <StyledLink to={`/vitals/${category.uid}/${article.uid}/`} aria-label={`Read ${title}`} onClick={() => callback()}>
              {title}
            </StyledLink>
          </ArticleTitle>
          <Description $noMobileDesign={noMobileDesign}>{article_subheadline}</Description>
        </TextWrapper>
      </InnerContainer>
    </Container>
  );
};

export default ArticleItem;

const Container = styled.div<{ $noMobileDesign?: boolean }>`
  width: 270px;
  display: flex;
  margin-right: 60px;
  justify-content: flex-start;

  ${({ $noMobileDesign }) =>
    $noMobileDesign
      ? ''
      : `@media (max-width: ${BREAKPOINT}px) {
    width: 100%;
    margin-right: 0;
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid ${Colors.ecru};
    position: relative;
    left: auto;
    top: auto;
  }
  
  @media (max-width: 1200px) {
    margin-bottom: 40px;
  }
  `}
`;

const InnerContainer = styled.div<{ $noMobileDesign?: boolean }>`
  max-width: 500px;
  display: flex;
  flex-direction: column;

  ${({ $noMobileDesign }) =>
    $noMobileDesign
      ? ''
      : `@media (max-width: ${BREAKPOINT}px) {
    width: 100%;
    margin: 0 auto;
    flex-direction: row;
  }`}
`;

const ImageWrapper = styled.div<{ $noMobileDesign?: boolean }>`
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
  aspect-ratio: 1/1;

  a {
    display: block;
  }

  img {
    width: 100%;
  }

  ${({ $noMobileDesign }) =>
    $noMobileDesign
      ? ''
      : `@media (max-width: ${BREAKPOINT}px) {
    a {
      position: absolute;
      height: 100%;
      width: 100%;
      z-index: 3;

      &:hover {
        opacity: 0.8;
      }
    }

    img {
      height: 100%;
      width: auto;
      object-fit: cover;
      position: absolute;
      z-index: 2;
    }

    padding-bottom: 0;
    margin-bottom: 0;
    margin-right: 20px;
    width: 100px;
    height: 100px;
    min-width: 100px;
  
    img {
      width: 100px;
      height: 100px;
    }
  }`}
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryName = styled.h6<{ $noMobileDesign?: boolean }>`
  font-family: 'BrownStd';
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 1px;
  text-align: left;
  color: ${Colors.lilac};
  margin-bottom: 10px;
  text-transform: uppercase;

  &:empty {
    display: none;
  }

  ${({ $noMobileDesign }) =>
    $noMobileDesign
      ? ''
      : `@media (max-width: ${BREAKPOINT}px) {
    font-size: 11px;
    line-height: 16.5px;
    margin-bottom: 3px;
  }`}
`;

const ArticleTitle = styled(H3)<{ $noMobileDesign?: boolean }>`
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 25px;
  letter-spacing: 0;
  text-align: left;
  margin-bottom: 10px;

  a {
    color: ${Colors.navyBlue};
  }

  &:hover a {
    color: ${Colors.lilac};
  }

  ${({ $noMobileDesign }) =>
    $noMobileDesign
      ? ''
      : `@media (max-width: ${BREAKPOINT}px) {
    font-size: 16px;
    line-height: 21px;
    letter-spacing: -0.02em;
    margin-bottom: 8px;
    font-weight: 500;
  }`}
`;

const Description = styled(P)<{ $noMobileDesign?: boolean }>`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  color: ${Colors.navyBlue};

  ${({ $noMobileDesign }) =>
    $noMobileDesign
      ? ''
      : `@media (max-width: ${BREAKPOINT}px) {
    font-size: 13px;
    line-height: 19.5px;
  }`}
`;

const StyledLink = styled(LinkTranslated)`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;
